import type {Article} from '../../types/article';

const ARTICLE_POST_FIELDS = `
  added
  content
  modified
  name
  postId
  props
  type
`;

export const SENTINEL_ARTICLES_QUERY = `
  query SentinelArticles($from: Int, $to: Int) {
    posts {
      articles: getPostsByLatest(from: $from, to: $to, type: "article") {
        ${ARTICLE_POST_FIELDS}
      }
      pdArticles: getPostsByLatest(from: $from, to: $to, type: "phantomdark") {
        ${ARTICLE_POST_FIELDS}
      }
      legacyPdArticles: getPostsByLatest(from: $from, to: $to, type: "pdarticle") {
        ${ARTICLE_POST_FIELDS}
      }
    }
  }
`;

export const SENTINEL_ARCHIVE_QUERY = `
  query SentinelArticleArchive($from: Int, $to: Int) {
    posts {
      articles: getPostsByLatest(from: $from, to: $to, type: "article") {
        ${ARTICLE_POST_FIELDS}
      }
      pdArticles: getPostsByLatest(from: $from, to: $to, type: "phantomdark") {
        ${ARTICLE_POST_FIELDS}
      }
      legacyPdArticles: getPostsByLatest(from: $from, to: $to, type: "pdarticle") {
        ${ARTICLE_POST_FIELDS}
      }
    }
  }
`;

interface SentinelPostLists {
  readonly articles?: unknown;
  readonly legacyPdArticles?: unknown;
  readonly pdArticles?: unknown;
}

export interface SentinelArticlesResponse {
  readonly data?: {readonly posts?: SentinelPostLists};
  readonly errors?: Array<{readonly message?: string}>;
}

interface SentinelArchiveResponse {
  readonly data?: {readonly posts?: SentinelPostLists};
  readonly errors?: Array<{readonly message?: string}>;
}

interface ArticleGraphqlRequest {
  readonly query: string;
  readonly variables?: Readonly<Record<string, unknown>>;
}

interface LoadStoredArticlesOptions {
  readonly endpoint: string;
  readonly post: (endpoint: string, request: ArticleGraphqlRequest) => Promise<SentinelArticlesResponse>;
}

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const readString = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() ? value : undefined;

const readHttpUrl = (value: unknown): string | undefined => {
  const url = readString(value);
  if(!url) return undefined;
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? parsed.toString() : undefined;
  } catch {
    return undefined;
  }
};

const parseArticlePost = (value: unknown): Article | undefined => {
  if(
    !isRecord(value)
    || !['article', 'phantomdark', 'pdarticle'].includes(value.type as string)
    || !isRecord(value.props)
  ) return undefined;
  const props = value.props;
  if(props.isActive === false || props.isFixture === true) return undefined;
  const id = readString(value.postId);
  const headline = readString(value.name) ?? readString(props.headline);
  const description = readString(props.description) ?? readString(value.content);
  const requiredProps = ['canonicalUrl', 'publishedAt', 'section', 'slug', 'sourceName', 'sourceType'] as const;
  if(!id || !headline || !description || !requiredProps.every((field) => readString(props[field]))) return undefined;

  const imageUrl = isRecord(props.image) ? readHttpUrl(props.image.url) : undefined;
  const image = isRecord(props.image) && imageUrl ? {
    alt: readString(props.image.alt) ?? '',
    credit: readString(props.image.credit) ?? '',
    external: props.image.external === true,
    url: imageUrl
  } : undefined;

  const author = isRecord(props.author) && readString(props.author.name) ? {
    name: readString(props.author.name) as string,
    role: readString(props.author.role)
  } : undefined;

  const references = Array.isArray(props.references) ? props.references.flatMap((reference) => {
    if(!isRecord(reference)) return [];
    const label = readString(reference.label);
    const url = readString(reference.url);
    return label && url ? [{label, url}] : [];
  }) : undefined;

  const body = Array.isArray(props.body) ? props.body.filter((paragraph): paragraph is string => typeof paragraph === 'string') : [];

  return {
    ...(author ? {author} : {}),
    ...(body.length ? {body} : {}),
    canonicalUrl: readString(props.canonicalUrl) as string,
    description,
    ...(readString(props.disclosure) ? {disclosure: readString(props.disclosure)} : {}),
    ...(readString(props.excerpt) ? {excerpt: readString(props.excerpt)} : {}),
    headline,
    id,
    image,
    ...(props.isActive !== undefined ? {isActive: props.isActive === true} : {}),
    ...(props.isEditorial === true ? {isEditorial: true} : {}),
    ...(props.isFixture === true ? {isFixture: true} : {}),
    publishedAt: readString(props.publishedAt) as string,
    ...(references?.length ? {references} : {}),
    section: readString(props.section) as string,
    slug: readString(props.slug) as string,
    sourceName: readString(props.sourceName) as string,
    sourceType: readString(props.sourceType) as Article['sourceType'],
    sourceUrl: readString(props.sourceUrl),
    tags: Array.isArray(props.tags) ? props.tags.filter((tag): tag is string => typeof tag === 'string') : [],
    updatedAt: readString(props.updatedAt)
  };
};

const parsePostList = (posts: unknown, invalidResponseMessage: string): Article[] => {
  if(!Array.isArray(posts)) throw new Error(invalidResponseMessage);
  return posts.flatMap((post) => {
    const article = parseArticlePost(post);
    return article ? [article] : [];
  });
};

const parseTypedPostLists = (
  posts: SentinelPostLists | undefined,
  invalidResponseMessage: string
): Article[] => {
  if(
    !posts
    || !Array.isArray(posts.articles)
    || !Array.isArray(posts.pdArticles)
    || !Array.isArray(posts.legacyPdArticles)
  ) {
    throw new Error(invalidResponseMessage);
  }
  const parsed = parsePostList(
    [...posts.articles, ...posts.pdArticles, ...posts.legacyPdArticles],
    invalidResponseMessage
  );
  return [...new Map(parsed.map((article) => [article.id, article])).values()];
};

export const parseArticlePosts = (posts: unknown): Article[] =>
  parsePostList(posts, 'The article service returned an invalid post list.');

export const parseSentinelArticlesResponse = (response: SentinelArticlesResponse): Article[] => {
  if(response.errors?.length) throw new Error(response.errors[0]?.message || 'The article service returned an error.');
  return parseTypedPostLists(
    response.data?.posts,
    'The article service returned an invalid response.'
  );
};

export const parseSentinelArchiveResponse = (response: SentinelArchiveResponse): Article[] => {
  if(response.errors?.length) throw new Error(response.errors[0]?.message || 'The article archive returned an error.');
  return parseTypedPostLists(
    response.data?.posts,
    'The article archive returned an invalid response.'
  );
};

export const loadStoredArticles = async ({
  endpoint,
  post
}: LoadStoredArticlesOptions): Promise<Article[]> =>
  post(endpoint, {
    query: SENTINEL_ARTICLES_QUERY,
    variables: {from: 0, to: 100}
  }).then((response) => parseSentinelArticlesResponse(response as SentinelArticlesResponse));

export const publicArticleEndpoint = (hostname: string): string => {
  if(hostname === 'localhost' || hostname === '127.0.0.1') return 'http://localhost:3401/public';
  const stage = hostname === 'localhost' || hostname.startsWith('local.') || hostname.startsWith('dev.') ? 'dev' : 'prod';
  return `https://api.chicagosentineltimes.com/${stage}/public`;
};
